import { fortName, shipName } from '@brig/nations';
import type { GameState, ShipClassId, Side } from '@brig/rules-engine';
import type { TFunction } from 'i18next';

/** The number at the end of a unit id ("red-brig-3" → "3"), if any. */
const numberOf = (id: string) => /-(\d+)$/.exec(id)?.[1] ?? null;

/**
 * A unit as players call it: a ship by class and own name, "Brig “Palomo”"; a fort by its
 * own name, "Fort Morro" (both by number, "Brig #3", "First-line fort 2", without one). The side
 * shows by flag and colour next to it wherever it matters, so the name does not repeat it.
 */
export function unitLabel(
  t: TFunction,
  state: GameState,
  nations: Readonly<Record<Side, string>>,
  id: string,
): string {
  const ship = state.ships.find((s) => s.id === id);
  if (ship) {
    const cls = className(t, ship.cls);
    const name = ownName(state, nations, id);
    if (name) return `${cls} ${t('game.unit.named', { name })}`;
    const n = numberOf(id);
    return n ? t('game.unit.ship', { cls, n }) : cls;
  }
  const fort = state.forts.find((f) => f.id === id);
  if (fort) {
    const name = ownName(state, nations, id);
    if (name) return t('game.unit.fort', { name });
    const n = numberOf(id) ?? fort.cell;
    return fort.cls === 'first_line' ? t('game.unit.fortFirst', { n }) : t('game.unit.fortSecond', { n });
  }
  return id;
}

/** A ship class as players call it: "Brig", "Ship of the line". */
export const className = (t: TFunction, cls: ShipClassId): string =>
  t(`ships.names.${cls}`).split(',')[0] ?? cls;

/** What a unit is, without its name: "Brig", "First-line fort". */
export function kindOf(t: TFunction, state: GameState, id: string): string {
  const ship = state.ships.find((s) => s.id === id);
  if (ship) return className(t, ship.cls);
  const fort = state.forts.find((f) => f.id === id);
  return fort ? t(`game.unit.fortClass.${fort.cls}`) : id;
}

/**
 * A unit's own name from its nation's lists (Palomo, Redoutable, Morro…), or null: the n-th
 * ship or fort of a class takes the n-th name (nations' shipNames.ts, fortNames.ts).
 */
export function ownName(
  state: GameState,
  nations: Readonly<Record<Side, string>>,
  id: string,
): string | null {
  const n = Number(numberOf(id));
  if (!(n > 0)) return null;
  const ship = state.ships.find((s) => s.id === id);
  if (ship) return shipName(nations[ship.side], ship.cls, n);
  const fort = state.forts.find((f) => f.id === id);
  return fort ? fortName(nations[fort.side], fort.cls, n) : null;
}
